'use client';
import { useCallback, useContext, useEffect } from 'react';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';
import type { Authentication } from '@benzinga/session';
import type { SafeType } from '@benzinga/safe-await';

import type { AuthAction, AuthMethod, AuthSuccessEventDetail, AuthSurface } from '../types';
import { emitAuthSuccess } from '../utils/emitAuthSuccess';

export interface UseAuthTrackingInput {
  surface: AuthSurface;
}

export interface TrackAuthSuccessInput {
  method: AuthMethod;
  action: AuthAction;
  auth: SafeType<Authentication>;
  isNewUserOverride?: boolean;
}

export interface UseAuthTrackingReturn {
  trackAuthSuccess: (input: TrackAuthSuccessInput) => Promise<AuthSuccessEventDetail | null>;
  trackSocialReturn: (
    searchParams: URLSearchParams,
    auth: SafeType<Authentication>,
  ) => Promise<AuthSuccessEventDetail | null>;
}

const BACKEND_TO_METHOD: Record<string, AuthMethod> = {
  'apple-id': 'apple',
  'google-oauth2': 'google',
  'google-one-tap': 'google-one-tap',
  'microsoft-graph': 'microsoft',
};

export const useAuthTracking = ({ surface }: UseAuthTrackingInput): UseAuthTrackingReturn => {
  const session = useContext(SessionContext);

  useEffect(() => {
    const tracking = session.getManager(TrackingManager);
    const isPaywalled = surface === 'paywall';
    const isRegwalled = surface === 'regwall';
    tracking.updateContext({ isPaywalled, isRegwalled });
    return () => {
      tracking.updateContext({ isPaywalled: false, isRegwalled: false });
    };
  }, [session, surface]);

  const trackAuthSuccess = useCallback<UseAuthTrackingReturn['trackAuthSuccess']>(
    async ({ action, auth, isNewUserOverride, method }) => {
      const user = auth?.ok?.user;
      if (!user) return null;

      const tracking = session.getManager(TrackingManager);
      await tracking.setUser(user);
      await tracking.trackAuthEvent(action, {
        auth_type: method,
        is_paywalled: surface === 'paywall',
        is_regwalled: surface === 'regwall',
      });

      const isNewUser = isNewUserOverride ?? action === 'register';
      const detail: AuthSuccessEventDetail = {
        action,
        isNewUser,
        method,
        surface,
        userId: user?.id ? String(user.id) : undefined,
      };

      emitAuthSuccess(detail);
      return detail;
    },
    [session, surface],
  );

  const trackSocialReturn = useCallback<UseAuthTrackingReturn['trackSocialReturn']>(
    async (searchParams, auth) => {
      const backend = searchParams.get('backend') ?? '';
      const method: AuthMethod = BACKEND_TO_METHOD[backend] ?? 'social-fallback';
      const newUserParam = searchParams.get('new_user');
      // Backend sends the literal string 'False' for returning users.
      const isNewUser = newUserParam !== null && newUserParam !== 'False';
      const action: AuthAction = isNewUser ? 'register' : 'login';
      return trackAuthSuccess({ action, auth, isNewUserOverride: isNewUser, method });
    },
    [trackAuthSuccess],
  );

  return { trackAuthSuccess, trackSocialReturn };
};
